import React from 'react';
import BaseFactory from './BaseFactory';
import {
  getCityAnchorTag,
  getBlogAnchorTag,
  getFaqsAnchorTag,
  getSolvAnchorTag,
  hasLocationHours,
  getNextOpenDayInfo,
  getNumLocationsWithinBrand,
  getClinicAnchorTag,
  getLocationDistanceInMiles,
  getBrandName,
  getNearbyBrandLocation,
  hasBrandName,
  NoTopMarginParagraph,
  NoBottomMarginParagraph,
  hasCustomDescription,
} from './util';
import { isEmptyArray } from '../../../core/util/array';
import { isEmptyObject } from '../../../core/util/object';
import CustomDescriptionSection from './CustomDescriptionSection';
import { SOLV_SECTION_TITLE } from './constants';

class PartnerUrgentCare extends BaseFactory {
  constructor(location, nearbyLocations, properties) {
    super(location, properties);
    this.nearbyLocations = nearbyLocations;
    this.ratingCountStr = this.ratingCount > 0 ? ` based on ${this.ratingCount} total ratings` : '';

    const nearbyBrandLocation = getNearbyBrandLocation(nearbyLocations);

    if (!isEmptyObject(nearbyBrandLocation)) {
      this.brandName = getBrandName(nearbyBrandLocation);
      this.numAffiliates = getNumLocationsWithinBrand(nearbyBrandLocation);
      this.nearbyBrandClinicAnchorTag = getClinicAnchorTag(nearbyBrandLocation, 'clinic');
      this.nearbyBrandClinicCityAnchorTag = getCityAnchorTag(nearbyBrandLocation);
      this.nearbyBrandClinicDistance = getLocationDistanceInMiles(nearbyBrandLocation);

      const nearbyBrandClinicNextOpenDayInfo = getNextOpenDayInfo(nearbyBrandLocation);
      this.nearbyBrandClinicNextOpenDayName = nearbyBrandClinicNextOpenDayInfo.name;
      this.nearbyBrandClinicNextOpenDayHoursStr = nearbyBrandClinicNextOpenDayInfo.openHoursString;
    }
  }

  getDescription() {
    return (
      `Book online at ${this.clinicName}, one of ${this.city}'s best ${this.clinicalCategory} ` +
      `locations at ${this.address}, ${this.city}, ${this.state}, ${this.zip}. Walk-in ` +
      'patients with non-emergent healthcare conditions welcome. For more information, call ' +
      `${this.clinicName}${this.phoneString}.`
    );
  }

  getKeywords() {
    return (
      `${this.clinicName}, ${this.clinicName}, ${this.city}, ${this.clinicName} ${this.zip}, ` +
      `urgent care ${this.city}, urgent care ${this.zip}, walk-in clinic ${this.city}, ` +
      `walk-in clinic ${this.zip}, ${this.city} ${this.state} clinic, ${this.zip} clinic`
    );
  }

  getBody() {
    return (
      <span>
        <CustomDescriptionSection location={this.location} />
        <NoTopMarginParagraph>
          {hasCustomDescription(this.location) && (
            <span>
              {SOLV_SECTION_TITLE}
              <br />
            </span>
          )}
          {this.clinicName} is an <b>urgent care center in {this.cityAnchorTag}</b>, located at{' '}
          {this.fullAddress}.{' '}
          {hasLocationHours(this.location) && (
            <span>
              They are open {this.numDaysOpenString} a week, including {this.nextOpenDayName} from{' '}
              {this.nextOpenDayHoursStr} seeing walk-in patients with non-emergent healthcare
              conditions.{' '}
            </span>
          )}
          Unlike most urgent care and walk-in clinics, {this.clinicName} offers you the ability to{' '}
          <b>book online</b> in advance through {getSolvAnchorTag()} prior to arriving at the
          clinic. While most urgent care patients can expect a short wait time, booking online
          should cut that wait considerably and get you out and on with your day more quickly
        </NoTopMarginParagraph>
        {hasBrandName(this.nearbyLocations) && (
          <p>
            As part of the larger {this.brandName}, this clinic has {this.numAffiliates} affiliates
            with the closest other {this.nearbyBrandClinicAnchorTag} being in{' '}
            {this.nearbyBrandClinicCityAnchorTag} about {this.nearbyBrandClinicDistance} miles away
            and is open {this.nearbyBrandClinicNextOpenDayName} from{' '}
            {this.nearbyBrandClinicNextOpenDayHoursStr}.
          </p>
        )}
        {this.rating && (
          <p>
            Recognized favorably amongst their patients, {this.clinicName} has an average rating of{' '}
            {this.rating} stars out of 5{this.ratingCountStr}.
            {!isEmptyArray(this.glowingWords) && (
              <span>
                {' '}
                In fact, recent patients have frequently used words such as{' '}
                {this.glowingWordsString} to describe their experience at the clinic. Certainly not
                terms you hear about healthcare providers too frequently!
              </span>
            )}
          </p>
        )}
        <NoBottomMarginParagraph>
          If you are looking to learn more about urgent care including statistics, trends,{' '}
          {getFaqsAnchorTag()} and other useful information, check out our {getBlogAnchorTag()} to
          empower your knowledge as a healthcare consumer. {this.cityGuideContent}
        </NoBottomMarginParagraph>
      </span>
    );
  }
}

export default PartnerUrgentCare;
